import { operatorText } from './validation.types.js';
import { EMAIL_MAX_LENGTH, EMAIL_REGEX, URL_REGEX } from './validators.js';
export { operatorText } from './validation.types.js';
// Re-export standalone validators
export { isValidEmail, isValidUrl, isValidWalletAddress, isValidNonEmptyString, isValidUuid, EMAIL_REGEX, EMAIL_MAX_LENGTH, URL_REGEX, WALLET_ADDRESS_REGEX, UUID_REGEX, } from './validators.js';
// ============================================================================
// Comparison Functions
// ============================================================================
/**
 * Compare two values using the specified operator.
 * Works with numbers, dates, and strings.
 */
export const compareValues = (a, b, op) => {
    switch (op) {
        case '<': return a < b;
        case '<=': return a <= b;
        case '>': return a > b;
        case '>=': return a >= b;
        case '==': return a === b;
        case '!=': return a !== b;
    }
};
/** Get human-readable operator text for error messages. */
export const getOperatorText = (op) => operatorText[op];
// ============================================================================
// Single Field Validation
// ============================================================================
/**
 * Validate a single field value against validation rules.
 * Does not check cross-field comparisons (use validateFieldComparisons for that).
 */
export const validateFieldValue = (value, rules, fieldType) => {
    const stringValue = value !== null && value !== undefined ? String(value) : '';
    const isEmpty = value === null || value === undefined || value === '';
    // Skip validation if empty - required-ness is determined by requiredUserFields
    // (the merged field on RedemptionDTO), not by a validation rule.
    if (isEmpty) {
        return null;
    }
    // Type-specific validation (happens regardless of rules)
    // Rules object only controls additional constraints (min/max, length, etc.)
    switch (fieldType) {
        case 'text':
            if (rules?.minLength && stringValue.length < rules.minLength) {
                return { field: '', message: `Minimum ${rules.minLength} characters required`, code: 'minLength' };
            }
            if (rules?.maxLength && stringValue.length > rules.maxLength) {
                return { field: '', message: `Maximum ${rules.maxLength} characters allowed`, code: 'maxLength' };
            }
            if (rules?.pattern && !new RegExp(rules.pattern).test(stringValue)) {
                return { field: '', message: rules.patternMessage || 'Invalid format', code: 'pattern' };
            }
            break;
        case 'email':
            if (stringValue.length > EMAIL_MAX_LENGTH || !EMAIL_REGEX.test(stringValue)) {
                return { field: '', message: 'Invalid email format', code: 'email' };
            }
            if (rules?.maxLength && stringValue.length > rules.maxLength) {
                return { field: '', message: `Maximum ${rules.maxLength} characters allowed`, code: 'maxLength' };
            }
            break;
        case 'url':
            if (!URL_REGEX.test(stringValue)) {
                return { field: '', message: 'Invalid URL format', code: 'url' };
            }
            if (rules?.maxLength && stringValue.length > rules.maxLength) {
                return { field: '', message: `Maximum ${rules.maxLength} characters allowed`, code: 'maxLength' };
            }
            break;
        case 'number':
            const numValue = typeof value === 'number' ? value : parseFloat(stringValue);
            // Check if string contains non-numeric garbage
            if (isNaN(numValue) || (typeof value === 'string' && !/^-?\d*\.?\d+$/.test(stringValue.trim()))) {
                return { field: '', message: 'Must be a valid number', code: 'type' };
            }
            if (rules?.min !== undefined && numValue < rules.min) {
                return { field: '', message: `Minimum value is ${rules.min}`, code: 'min' };
            }
            if (rules?.max !== undefined && numValue > rules.max) {
                return { field: '', message: `Maximum value is ${rules.max}`, code: 'max' };
            }
            break;
        case 'boolean':
            if (typeof value !== 'boolean' && stringValue !== 'true' && stringValue !== 'false') {
                return { field: '', message: 'Must be true or false', code: 'type' };
            }
            break;
        case 'date':
            const dateValue = new Date(stringValue);
            if (isNaN(dateValue.getTime())) {
                return { field: '', message: 'Must be a valid date', code: 'type' };
            }
            const now = new Date();
            if (rules?.minDate) {
                const minDate = rules.minDate === 'now' ? now : new Date(rules.minDate);
                if (dateValue < minDate) {
                    return {
                        field: '',
                        message: rules.minDate === 'now' ? 'Date must be in the future' : `Date must be after ${rules.minDate}`,
                        code: 'minDate'
                    };
                }
            }
            if (rules?.maxDate) {
                const maxDate = rules.maxDate === 'now' ? now : new Date(rules.maxDate);
                if (dateValue > maxDate) {
                    return {
                        field: '',
                        message: rules.maxDate === 'now' ? 'Date must be in the past' : `Date must be before ${rules.maxDate}`,
                        code: 'maxDate'
                    };
                }
            }
            break;
        case 'phone':
            // Basic phone validation - allows digits, spaces, dashes, parentheses, plus
            const phoneRegex = /^[\d\s\-\(\)\+]+$/;
            if (!phoneRegex.test(stringValue)) {
                return { field: '', message: 'Invalid phone number format', code: 'phone' };
            }
            if (rules?.minLength && stringValue.replace(/\D/g, '').length < rules.minLength) {
                return { field: '', message: `Phone number too short`, code: 'minLength' };
            }
            if (rules?.maxLength && stringValue.length > rules.maxLength) {
                return { field: '', message: `Maximum ${rules.maxLength} characters allowed`, code: 'maxLength' };
            }
            break;
        case 'select':
            // Single selection - just validate it's a string (option validation done separately)
            if (typeof value !== 'string') {
                return { field: '', message: 'Must be a string', code: 'type' };
            }
            break;
        case 'multiselect':
            if (!Array.isArray(value)) {
                return { field: '', message: 'Must be an array', code: 'type' };
            }
            if (rules?.minItems !== undefined && value.length < rules.minItems) {
                return { field: '', message: `Select at least ${rules.minItems} options`, code: 'minItems' };
            }
            if (rules?.maxItems !== undefined && value.length > rules.maxItems) {
                return { field: '', message: `Select at most ${rules.maxItems} options`, code: 'maxItems' };
            }
            // Validate each item is a string
            if (!value.every(item => typeof item === 'string')) {
                return { field: '', message: 'All selections must be strings', code: 'type' };
            }
            break;
    }
    return null;
};
// ============================================================================
// Cross-Field / Literal Comparison Validation
// ============================================================================
/** Type guard for field comparison */
const isFieldComparison = (c) => 'field' in c && c.field !== undefined;
/**
 * Validate comparison rules for a single field.
 * Supports comparing to another field OR a literal value.
 */
export const validateFieldComparisons = (fieldKey, value, comparisons, allData, fieldType) => {
    if (!comparisons || comparisons.length === 0)
        return [];
    const errors = [];
    for (const comparison of comparisons) {
        // Skip if this field's value is empty
        if (value === null || value === undefined || value === '')
            continue;
        // Get the comparison target (field value or literal)
        const isField = isFieldComparison(comparison);
        const targetValue = isField ? allData[comparison.field] : comparison.value;
        const targetLabel = isField ? comparison.field : String(comparison.value);
        // Skip if comparing to field and that field is empty
        if (isField && (targetValue === null || targetValue === undefined || targetValue === ''))
            continue;
        let isValid = true;
        const { operator, message } = comparison;
        switch (fieldType) {
            case 'date': {
                const thisDate = new Date(String(value));
                const otherDate = new Date(String(targetValue));
                if (isNaN(thisDate.getTime()) || isNaN(otherDate.getTime()))
                    continue;
                isValid = compareValues(thisDate.getTime(), otherDate.getTime(), operator);
                break;
            }
            case 'number': {
                const thisNum = typeof value === 'number' ? value : parseFloat(String(value));
                const otherNum = typeof targetValue === 'number' ? targetValue : parseFloat(String(targetValue));
                if (isNaN(thisNum) || isNaN(otherNum))
                    continue;
                isValid = compareValues(thisNum, otherNum, operator);
                break;
            }
            case 'boolean': {
                const thisBool = typeof value === 'boolean' ? value : value === 'true';
                const otherBool = typeof targetValue === 'boolean' ? targetValue : targetValue === 'true';
                isValid = operator === '==' ? thisBool === otherBool :
                    operator === '!=' ? thisBool !== otherBool : true;
                break;
            }
            default: {
                isValid = compareValues(String(value), String(targetValue), operator);
                break;
            }
        }
        if (!isValid) {
            errors.push({
                field: fieldKey,
                message: message || `Must be ${getOperatorText(operator)} ${targetLabel}`,
                code: `comparison:${operator}:${targetLabel}`,
            });
        }
    }
    return errors;
};
// ============================================================================
// Full Data Validation
// ============================================================================
/**
 * Validate all fields in a data object against their definitions.
 * Runs both single-field and comparison validations.
 *
 * @param data - The data object to validate
 * @param definitions - Field definitions with validation rules
 * @returns Array of all validation errors
 */
export const validateAllFields = (data, definitions) => {
    const errors = [];
    const definitionMap = new Map(definitions.map(d => [d.key, d]));
    for (const [key, value] of Object.entries(data)) {
        const def = definitionMap.get(key);
        if (!def)
            continue;
        // Single-field validation
        const error = validateFieldValue(value, def.validation, def.fieldType);
        if (error) {
            errors.push({ ...error, field: key });
        }
        // Comparison validation
        if (def.validation?.comparisons) {
            const comparisonErrors = validateFieldComparisons(key, value, def.validation.comparisons, data, def.fieldType);
            errors.push(...comparisonErrors);
        }
    }
    return errors;
};
//# sourceMappingURL=field-validation.js.map